import { forwardRef, type HTMLAttributes } from 'react';

import type { IconId } from '@tmh-website/components/icons/phosphor-extension';
import type { LinkField } from '@tmh-website/shared/cms';
import type { LocalePrefix } from '@tmh-website/shared/const';
import { IconStandalone } from '@tmh-website/components/icons/phosphor-extension/IconStandalone';
import { useMaskLink } from '@tmh-website/services/link.service';

import { HeaderThemeColor } from '../Header.theme';

import * as Styled from '../NavigationHighlightLink/NavigationHighlightLink.styles';

interface NavigationHighlightLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  headerTheme: HeaderThemeColor;
  iconId: IconId;
  link: LinkField;
  locale: LocalePrefix;

  /**
   * Mask the link using base64 encoding. This is used for SEO optimization
   */
  maskLink?: boolean;
}

const NavigationHighlightLinkStandalone = forwardRef<HTMLAnchorElement, NavigationHighlightLinkProps>(
  ({ link, maskLink = false, headerTheme, iconId, locale, ...props }, ref) => {
    const href = '/' + locale + link.value;
    const { renderedHref, onMouseEnter, onMouseLeave } = useMaskLink(maskLink, href);

    if (href) {
      return (
        <Styled.Container
          {...props}
          href={renderedHref}
          as={renderedHref ? 'a' : 'button'}
          onMouseEnter={e => {
            if (onMouseEnter) onMouseEnter();
            if (props.onMouseEnter) props.onMouseEnter(e);
          }}
          onMouseLeave={e => {
            if (onMouseLeave) onMouseLeave();
            if (props.onMouseLeave) props.onMouseLeave(e);
          }}
          $themeColor={headerTheme}
          ref={ref}>
          <IconStandalone iconId={iconId} size={32} />
          <Styled.Label $style='uiText'>{link.label}</Styled.Label>
        </Styled.Container>
      );
    }
    return null;
  }
);

NavigationHighlightLinkStandalone.displayName = 'NavigationHighlightLinkStandalone';

export default NavigationHighlightLinkStandalone;
